import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Avatar,
  Box,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Favorite, 
  FavoriteBorder, 
  Share, 
  Star, 
  StarBorder,
  Verified
} from '@mui/icons-material';
import styled from '@emotion/styled';

const ProfileHeader = styled.div`
  background: linear-gradient(45deg, #FF385C 30%, #FF6B6B 90%);
  padding: 40px 0;
  color: white;
  margin-bottom: 30px;

  @media (max-width: 600px) {
    padding: 20px 0;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 15px;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 40px;
  margin-top: 20px;

  @media (max-width: 600px) {
    gap: 20px;
    justify-content: center;
    margin-top: 15px;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const PostCard = styled(Card)`
  height: 100%;
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-5px);
  }
`;

interface User {
  username: string;
  display_name: string;
  bio: string;
  avatar_url: string;
  followers_count: number;
  posts_count: number;
  satisfaction_rate: number;
  is_verified: boolean;
}

interface Post {
  id: number;
  title: string;
  description: string;
  image_url: string;
  likes_count: number;
  comments_count: number;
  is_liked: boolean;
  is_starred: boolean;
}

const UserProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user data
        const userResponse = await fetch(`http://localhost:5001/api/users/${username}`);
        if (!userResponse.ok) {
          throw new Error('User not found');
        }
        const userData = await userResponse.json();
        setUser(userData);
        
        // Fetch user posts
        const postsResponse = await fetch(`http://localhost:5001/api/users/${username}/posts`);
        if (!postsResponse.ok) {
          throw new Error('Failed to fetch posts');
        }
        const postsData = await postsResponse.json();
        setPosts(postsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [username]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div>
      <ProfileHeader>
        <Container maxWidth="lg">
          <ProfileInfo>
            <Avatar
              src={user.avatar_url || "/images/profile-avatar.jpg"}
              sx={{ 
                width: { xs: 100, sm: 120 }, 
                height: { xs: 100, sm: 120 }, 
                border: '4px solid white' 
              }}
            />
            <Box sx={{ width: '100%' }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  wordBreak: 'break-word'
                }}
              >
                {user.display_name || user.username}
                {user.is_verified && <Verified sx={{ color: '#FFD700', flexShrink: 0 }} />}
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  display: { xs: 'block', sm: 'inline' },
                  textAlign: { xs: 'center', sm: 'left' },
                  wordBreak: 'break-word'
                }}
              >
                {user.bio}
              </Typography>
              <StatsContainer>
                <StatItem>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    {user.followers_count.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">Followers</Typography>
                </StatItem>
                <StatItem>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    {user.posts_count.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">Posts</Typography>
                </StatItem>
                <StatItem>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    {user.satisfaction_rate}%
                  </Typography>
                  <Typography variant="body2">Satisfaction</Typography>
                </StatItem>
              </StatsContainer>
            </Box>
          </ProfileInfo>
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, mt: { xs: 2, sm: 0 } }}>
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: 'white', 
                color: '#FF385C',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                },
                padding: { xs: '8px 16px', sm: '12px 24px' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Subscribe $9.99/month
            </Button>
          </Box>
        </Container>
      </ProfileHeader>

      <Container maxWidth="lg">
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          Featured Content
          <Star sx={{ color: '#FFD700' }} />
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ px: { xs: 2, sm: 0 } }}>
          {posts.map((post) => (
            <Grid item xs={12} sm={6} md={3} key={post.id}>
              <PostCard>
                <CardMedia
                  component="img"
                  height="200"
                  image={post.image_url}
                  alt={post.title}
                  sx={{
                    objectFit: 'cover',
                    height: { xs: '250px', sm: '200px' }
                  }}
                />
                <CardContent>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    {post.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {post.description}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 'auto'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small">
                        {post.is_liked ? <Favorite color="error" /> : <FavoriteBorder />}
                      </IconButton>
                      <Typography variant="body2" component="span">
                        {post.likes_count.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton size="small">
                        {post.is_starred ? <Star color="primary" /> : <StarBorder />}
                      </IconButton>
                      <IconButton size="small">
                        <Share />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </PostCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
};

export default UserProfile; 